import { Block } from '@/components/base/Block'
import InputErrorLabel from '@/components/base/Form/InputErrorLabel'
import { Text3 } from '@/components/base/Text'
import { align } from '@/styles/display'
import spaceStyle from '@/styles/space'
import { useMemo } from 'react'
import {
  type V1FormElementContentLabelAndState,
  type V1FormElementState,
  V1FormElementStateType,
  type V1FormElementUiSelectBox
} from 'mnvm-mypage-app-ts-if'
import GenericFormElementSelectBox, {
  type GenericFormElementSelectBoxOption,
  type SelectBoxStyle
} from './GenericFormElementSelectBox'

export interface FormElementSelectBoxProps {
  isEdit?: boolean
  selectBoxUiIFM: V1FormElementUiSelectBox
  formElementState: V1FormElementState
  elemenErrorMessage?: string
  formElementContents?: V1FormElementContentLabelAndState[]
  onChange: (newElementState: V1FormElementState) => void
  style?: SelectBoxStyle
  isHorizontalLayoutProfile?: boolean
  prefixLabel?: string
}

export const generateOptions = (
  formElementStateType: V1FormElementStateType,
  options: V1FormElementContentLabelAndState[]
): GenericFormElementSelectBoxOption[] => {
  return (
    options?.map((option) => {
      return {
        label: option.label ?? 'label',
        value:
          formElementStateType === V1FormElementStateType.Text
            ? option.state?.text ?? ''
            : option.state?.uint32 ?? 0
      }
    }) ?? []
  )
}

export const FormElementSelectBox = ({
  isEdit = true,
  selectBoxUiIFM,
  formElementState,
  formElementContents,
  elemenErrorMessage,
  onChange,
  style,
  isHorizontalLayoutProfile,
  prefixLabel = ''
}: FormElementSelectBoxProps): JSX.Element => {
  const selectedValue: number | string | undefined =
    formElementState.type === V1FormElementStateType.Text
      ? formElementState.contentStates?.at(0)?.text
      : formElementState.contentStates?.at(0)?.uint32 ?? -1

  let contents: V1FormElementContentLabelAndState[] = []
  if (formElementContents == null) {
    contents = selectBoxUiIFM.contents ?? []
  } else {
    contents = formElementContents
  }

  const isHorizontalLayout = isHorizontalLayoutProfile ?? false

  if (!isEdit) {
    const item = contents?.find(
      (e) =>
        (formElementState.type === V1FormElementStateType.Text
          ? e.state?.text
          : e.state?.uint32) === selectedValue
    )
    const label = item != null ? item.label : '-'

    return (
      <Block width={isHorizontalLayout ? '50px' : 'auto'}>
        <Text3
          textAlign={
            isHorizontalLayout
              ? formElementState.type === V1FormElementStateType.Text
                ? align.LEFT
                : align.RIGHT
              : align.LEFT
          }
          margin={{ all: spaceStyle.NONE }}
        >
          {prefixLabel}
          {label}
          {selectBoxUiIFM.unit}
        </Text3>
      </Block>
    )
  }

  if (formElementState.type == null) {
    return <></>
  }

  const options = generateOptions(formElementState.type, contents)

  const selectBoxWidth = 100

  const computedLabelWidth = useMemo(() => {
    const unit = selectBoxUiIFM.unit ?? ''
    const numChars = unit.length ?? 0
    return numChars > 0 ? numChars * 12 : 0
  }, [selectBoxUiIFM.unit])

  return (
    <>
      <div>
        <GenericFormElementSelectBox
          options={options}
          selectedValue={selectedValue}
          disabled={options.length <= 0}
          hasError={elemenErrorMessage !== undefined}
          onChange={(changedValue) => {
            if (changedValue === undefined) {
              onChange({
                ...formElementState,
                contentStates: []
              })
            } else {
              onChange({
                ...formElementState,
                contentStates: [
                  formElementState.type === V1FormElementStateType.Text
                    ? {
                        text: changedValue as string
                      }
                    : {
                        uint32: Number(changedValue)
                      }
                ]
              })
            }
          }}
          label={{ right: selectBoxUiIFM.unit }}
          style={style}
          labelWidth={
            isHorizontalLayout || selectBoxUiIFM.unit !== undefined
              ? computedLabelWidth
              : 0
          }
          selectBoxWidth={isHorizontalLayout ? selectBoxWidth : 0}
        />
        {elemenErrorMessage !== undefined && (
          <InputErrorLabel message={elemenErrorMessage} />
        )}
      </div>
    </>
  )
}
